import React, { useState, useEffect } from 'react';
import './Home.css';

const translations = {
  en: {
    enterName: 'Enter your name',
    createLobby: 'Create Lobby',
    or: 'OR',
    enterCode: 'Enter lobby code',
    joinLobby: 'Join Lobby',
    howToPlay: 'How to Play!',
    rejoin: 'Rejoin',
    newGame: 'New Game',
    continueAs: 'Continue as',
    activeSession: 'You have an active game session',
    modalTitle: 'How to Play Ride the Bus!',
    objective: 'Objective',
    objectiveText: 'Survive three phases without collecting too many cards. The player with the most cards rides the bus!',
    phase1: 'Phase 1: Four Questions',
    phase1Q1: 'Red or Black?',
    phase1Q1Desc: 'Guess the card color',
    phase1Q2: 'Higher or Lower?',
    phase1Q2Desc: 'Compared to your first card',
    phase1Q3: 'Inside or Outside?',
    phase1Q3Desc: 'Between your first two cards',
    phase1Q4: 'Suit?',
    phase1Q4Desc: 'Hearts, Diamonds, Clubs, or Spades',
    wrongGuess: 'Wrong guess = drink and get a card!',
    phase2: 'Phase 2: The Pyramid',
    phase2Text: 'Cards are revealed row by row. Match a card = give drinks equal to the row number!',
    phase2Row1: 'Bottom row (4 cards) = 1 drink each',
    phase2Row2: 'Next row (3 cards) = 2 drinks each',
    phase2Row3: 'Next row (2 cards) = 3 drinks each',
    phase2Row4: 'Top row (1 card) = 4 drinks!',
    timerText: 'Timer runs - if no one matches, targeted player drinks!',
    phase3: 'Phase 3: Ride the Bus',
    phase3Text: 'Player with most cards must guess 4 cards correctly:',
    phase3Wrong: 'Wrong guess = drink and start over!',
    tips: 'Tips',
    tip1: 'Play strategically in Phase 1 to avoid cards',
    tip2: 'Use your cards wisely in the pyramid',
    tip3: 'The bus rider drinks the most - avoid it!',
    gotIt: 'Got it!'
  },
  sv: {
    enterName: 'Ange ditt namn',
    createLobby: 'Skapa Lobby',
    or: 'ELLER',
    enterCode: 'Ange lobbykod',
    joinLobby: 'Gå med i Lobby',
    howToPlay: 'Hur man spelar!',
    rejoin: 'Återanslut',
    newGame: 'Nytt Spel',
    continueAs: 'Fortsätt som',
    activeSession: 'Du har en aktiv spelsession',
    modalTitle: 'Hur man spelar Ride the Bus!',
    objective: 'Mål',
    objectiveText: 'Överlev tre faser utan att samla för många kort. Spelaren med flest kort åker bussen!',
    phase1: 'Fas 1: Fyra Frågor',
    phase1Q1: 'Röd eller Svart?',
    phase1Q1Desc: 'Gissa kortets färg',
    phase1Q2: 'Högre eller Lägre?',
    phase1Q2Desc: 'Jämfört med ditt första kort',
    phase1Q3: 'Innanför eller Utanför?',
    phase1Q3Desc: 'Mellan dina två första kort',
    phase1Q4: 'Färg?',
    phase1Q4Desc: 'Hjärter, Ruter, Klöver eller Spader',
    wrongGuess: 'Fel gissning = drick och få ett kort!',
    phase2: 'Fas 2: Pyramiden',
    phase2Text: 'Kort vänds rad för rad. Match = ge drinkar lika med radnumret!',
    phase2Row1: 'Nedersta raden (4 kort) = 1 drink vardera',
    phase2Row2: 'Nästa rad (3 kort) = 2 drinkar vardera',
    phase2Row3: 'Nästa rad (2 kort) = 3 drinkar vardera',
    phase2Row4: 'Översta raden (1 kort) = 4 drinkar!',
    timerText: 'Timer tickar - om ingen matchar, dricker målspelaren!',
    phase3: 'Fas 3: Åk Bussen',
    phase3Text: 'Spelaren med flest kort måste gissa 4 kort rätt:',
    phase3Wrong: 'Fel gissning = drick och börja om!',
    tips: 'Tips',
    tip1: 'Spela strategiskt i Fas 1 för att undvika kort',
    tip2: 'Använd dina kort klokt i pyramiden',
    tip3: 'Bussåkaren dricker mest - undvik det!',
    gotIt: 'Fattar!'
  }
};

function Home({ socket, playerName, setPlayerName, setScreen, lobbyCode, setLobbyCode, setLobby, language = 'en', setLanguage, savedSession }) {
  const [joinCode, setJoinCode] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [mode, setMode] = useState('home');
  const [showRejoin, setShowRejoin] = useState(!!savedSession);
  const t = (key) => translations[language][key] || key;

  useEffect(() => {
    setShowRejoin(!!savedSession);
  }, [savedSession]);

  useEffect(() => {
    if (lobbyCode) {
      setJoinCode(lobbyCode);
      setMode('join');
    }
  }, [lobbyCode]);

  const handleRejoin = () => {
    if (savedSession) {
      setJoinCode(savedSession.code);
      
      // Set up one-time error handler for rejoin
      const errorHandler = ({ message }) => {
        if (message && (message.includes('not found') || message.includes('Lobby not found'))) {
          sessionStorage.removeItem('rtb_session');
          setPlayerName('');
          setLobbyCode('');
          setShowRejoin(false);
          alert('Session expired. Please create or join a new game.');
        }
        socket.off('error', errorHandler);
      };
      
      socket.on('error', errorHandler);
      
      socket.emit('joinLobby', { code: savedSession.code, playerName: savedSession.name });
      
      const lobbyUpdateHandler = (lobby) => {
        setLobbyCode(savedSession.code);
        setLobby(lobby);
        setScreen('lobby');
        socket.off('error', errorHandler);
        socket.off('lobbyUpdate', lobbyUpdateHandler);
        socket.off('joinedAsSpectator', spectatorHandler);
      };
      
      const spectatorHandler = ({ lobby }) => {
        setLobbyCode(savedSession.code);
        setLobby(lobby);
        setScreen('game');
        socket.off('error', errorHandler);
        socket.off('lobbyUpdate', lobbyUpdateHandler);
        socket.off('joinedAsSpectator', spectatorHandler);
      };
      
      socket.on('lobbyUpdate', lobbyUpdateHandler);
      socket.on('joinedAsSpectator', spectatorHandler);
    }
  };

  const handleDismissRejoin = () => {
    sessionStorage.removeItem('rtb_session');
    setPlayerName('');
    setLobbyCode('');
    setShowRejoin(false);
  };

  const handleCreateLobby = () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    socket.emit('createLobby', playerName);
  };

  const handleJoinLobby = () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!joinCode.trim()) {
      alert('Please enter a lobby code');
      return;
    }
    const upperCode = joinCode.toUpperCase();
    socket.emit('joinLobby', { code: upperCode, playerName });
    socket.on('lobbyUpdate', (lobby) => {
      setLobbyCode(upperCode);
      setLobby(lobby);
      setScreen('lobby');
    });

    socket.on('joinedAsSpectator', ({ lobby }) => {
      setLobbyCode(upperCode);
      setLobby(lobby);
      setScreen('game');
    });

    socket.on('spectatorJoined', ({ spectator }) => {
      console.log(`${spectator} joined as spectator`);
    });
  };

  return (
    <div className="home-container">
      <a href="https://www.partygamez.com" style={{position:'absolute',top:'1rem',left:'1rem',fontSize:'0.75rem',color:'rgba(255,255,255,0.3)',textDecoration:'none',zIndex:20}}>← PartyGamez.com</a>
      {setLanguage && (
        <button
          onClick={() => setLanguage(language === 'en' ? 'sv' : 'en')}
          className="language-toggle"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          🌐 {language.toUpperCase()}
        </button>
      )}
      <div className="home-content">
        <div className="title-section">
          <img src="/ridethebus/logo.png" alt="Ride The Bus" className="game-logo" />
        </div>

        {savedSession && (
          <div className="rejoin-banner" style={{
            background: 'rgba(0, 200, 255, 0.15)',
            border: '2px solid rgba(0, 200, 255, 0.4)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
              🔄 {t('continueAs')} <strong>{savedSession.name}</strong>?
            </p>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', opacity: 0.8 }}>
              {t('activeSession')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={handleDismissRejoin} className="btn" style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                padding: '8px 16px',
                fontSize: '14px'
              }}>
                {t('newGame')}
              </button>
              <button onClick={handleRejoin} className="btn btn-primary" style={{
                padding: '8px 16px',
                fontSize: '14px'
              }}>
                {t('rejoin')}
              </button>
            </div>
          </div>
        )}

        <div className="input-section">
          {mode === 'home' && (
            <>
              <button onClick={() => setMode('create')} className="btn btn-primary">
                {t('createLobby')}
              </button>
              <button onClick={() => setMode('join')} className="btn btn-secondary">
                {t('joinLobby')}
              </button>
              <button onClick={() => setShowHowToPlay(true)} className="btn btn-how-to-play">
                ℹ️ {t('howToPlay')}
              </button>
            </>
          )}

          {mode === 'create' && (
            <div style={{width:'100%',display:'flex',flexDirection:'column',gap:'10px'}}>
              <h3 style={{marginBottom:'1rem',color:'#00c8ff',textAlign:'center'}}>{t('createLobby')}</h3>
              <input
                type="text"
                placeholder={t('enterName')}
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                className="name-input"
                maxLength={20}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateLobby()}
                autoFocus
              />
              <button onClick={handleCreateLobby} className="btn btn-primary">
                {t('createLobby')}
              </button>
              <button onClick={() => setMode('home')} className="btn" style={{background:'rgba(255,255,255,0.1)',marginTop:'10px'}}>
                Back
              </button>
            </div>
          )}

          {mode === 'join' && (
            <div style={{width:'100%',display:'flex',flexDirection:'column',gap:'10px'}}>
              <h3 style={{marginBottom:'1rem',color:'#00c8ff',textAlign:'center'}}>{t('joinLobby')}</h3>
              <input
                type="text"
                placeholder={t('enterCode')}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="code-input"
                maxLength={6}
                style={{fontSize:'1.5rem',letterSpacing:'0.3em',textAlign:'center'}}
                onKeyDown={(e) => e.key === 'Enter' && playerName && handleJoinLobby()}
                autoFocus
              />
              <input
                type="text"
                placeholder={t('enterName')}
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                className="name-input"
                maxLength={20}
                onKeyDown={(e) => e.key === 'Enter' && joinCode && handleJoinLobby()}
              />
              <button onClick={handleJoinLobby} className="btn btn-secondary">
                {t('joinLobby')}
              </button>
              <button onClick={() => setMode('home')} className="btn" style={{background:'rgba(255,255,255,0.1)',marginTop:'10px'}}>
                Back
              </button>
            </div>
          )}
        </div>
      </div>

      <p style={{position:'absolute',bottom:'1rem',left:0,right:0,textAlign:'center',fontSize:'0.75rem',color:'rgba(255,255,255,0.2)',fontWeight:500}}>partygamez.com</p>

      {/* How to Play Modal */}
      {showHowToPlay && (
        <div className="modal-overlay" onClick={() => setShowHowToPlay(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowHowToPlay(false)}>×</button>
            <h2>{t('modalTitle')}</h2>
            <div className="modal-body">
              <h3>🎯 {t('objective')}</h3>
              <p>{t('objectiveText')}</p>
              
              <h3>📋 {t('phase1')}</h3>
              <ol>
                <li><strong>{t('phase1Q1')}</strong> - {t('phase1Q1Desc')}</li>
                <li><strong>{t('phase1Q2')}</strong> - {t('phase1Q2Desc')}</li>
                <li><strong>{t('phase1Q3')}</strong> - {t('phase1Q3Desc')}</li>
                <li><strong>{t('phase1Q4')}</strong> - {t('phase1Q4Desc')}</li>
              </ol>
              <p>{t('wrongGuess')}</p>
              
              <h3>🔺 {t('phase2')}</h3>
              <p>{t('phase2Text')}</p>
              <ul>
                <li>{t('phase2Row1')}</li>
                <li>{t('phase2Row2')}</li>
                <li>{t('phase2Row3')}</li>
                <li>{t('phase2Row4')}</li>
              </ul>
              <p>{t('timerText')}</p>
              
              <h3>🚌 {t('phase3')}</h3>
              <p>{t('phase3Text')}</p>
              <ol>
                <li>{t('phase1Q1')}</li>
                <li>{t('phase1Q2')}</li>
                <li>{t('phase1Q3')}</li>
                <li>{t('phase1Q4')}</li>
              </ol>
              <p>{t('phase3Wrong')}</p>
              
              <h3>💡 {t('tips')}</h3>
              <ul>
                <li>{t('tip1')}</li>
                <li>{t('tip2')}</li>
                <li>{t('tip3')}</li>
              </ul>
            </div>
            <button className="btn-modal-close" onClick={() => setShowHowToPlay(false)}>{t('gotIt')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
